import xpjsService from './xpjsService'
import utils from '@/library/geolocation/services/common/utils'
import GeolocationManager from '@/library/geolocation/services/geolocationManager'
import config from '@/config'
import Vue from 'vue'
import moment from 'moment/moment'
import helpers from '@/library/geolocation/services/providers/xpoint/helpers'

const isDebug = sessionStorage.geolocationDebug
let geoManager = null
let xpointExpirationTime = false
let timeoutInterval = false
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Checks the current XPoint Lite geolocation status.
 *
 * @param {String|Number} user - The user ID.
 * @param {String|Boolean} sessionId - Session identifier (currently unused).
 * @param {String} reason - Reason for the check ("Login", "Reload", "Periodic", "IP changed").
 * @param {String} CLIENT_KEY - Client identifier for XPoint API.
 * @param {Boolean} forceRefresh - Force a fresh geolocation in case of IP change.
 *
 * If the reason is "Reload", this function will not call the API but will
 * instead schedule a deferred check using the stored token information.
 */
async function checkStatus (user, sessionId, reason, CLIENT_KEY, forceRefresh = false) {
  if (isDebug && reason === 'Login') console.log('[Xpoint] Checking geolocation status...')
  if (isDebug && reason === 'IP changed') {
    forceRefresh = true
    disconnectService()
  }
  const options = {
    userId: user,
    client: CLIENT_KEY
  }
  // On browser refresh, use token expiration logic instead of checking immediately
  if (reason === 'Reload') {
    checkTokenValidity(options)
    return false
  }

  try {
    let data = await xpjsService.liteCheck(options, forceRefresh)
    // Validate geolocation result
    if (isDebug) console.log('[XPoint] Check status responded:', data)
    if (data && data.status && data.status === 'ALLOWED') xpointExpirationTime = moment()
    if (data.status === 'DENIED' && helpers.has5010Error(data)) {
      disconnectService()
      if (isDebug) console.log('[XPoint] Session has expired.')
      await sleep(2000)
      return checkStatus(user, false, reason, CLIENT_KEY)
    }
    // Validate JWT
    if (data.jwt && user) managerInstance.geolocationManager.validateGeolocation(data.jwt, user)
    return true
  } catch (e) {
    if (isDebug) console.error('[XPoint] liteCheck error:', e)
    return false
  }
}

/**
 * Schedules the next automatic geolocation check.
 *
 * @param {Object} options - Object containing userId and client.
 * @param {Object|Boolean} result - XPoint response containing expiration and next-check values.
 * @param {Number} timer - Optional predefined delay in ms.
 *
 * The function calculates delay based on:
 *  - token expiration time
 *  - XPoint's recommended "NextGeolocationInSeconds"
 *  - fallback: immediate check if token already expired
 */
function startStatusUpdate (options, result = false, timer = 0) {
  let timeout = timer
  if (result) {
    let expirationTime = result.Expires
    const expirationUTC = moment(expirationTime).utc()
    const xpointExpiration = moment(xpointExpirationTime).utc()
    // Provided buffer from XPoint
    timer = result.NextGeolocationInSeconds * 1000
    // Store token/check info
    localStorage.removeItem('tgc')
    let lcData = { 'date': xpointExpiration, 'expT': expirationUTC, 'cf': timer }
    localStorage.setItem('tgc', JSON.stringify(lcData))
    // Calculate differences
    const diffMs = expirationUTC.diff(xpointExpiration)
    // const diffSeconds = expirationUTC.diff(xpointExpiration, 'seconds')
    // const diffMin = diffMs / 1000 / 60
    // const diffMinutes = Number(diffMin.toFixed(2))
    // console.log('XPOINT different', {diffMs, diffSeconds, diffMinutes})
    if (diffMs > timer) {
      if (isDebug) console.log('[Xpoint] use buffered time.', timer)
      timeout = timer
    } else if (diffMs > 0 && diffMs <= timer) {
      timeout = diffMs
      if (isDebug) console.log('[Xpoint] set diff time.', timeout)
    } else {
      if (isDebug) console.log('XPOINT token expired.')
      timeout = 0
    }
  }
  // Schedule the next automatic check
  timeoutInterval = setTimeout(() => {
    checkStatus(options.userId, false, 'Periodic', options.client)
  }, timeout)
}

/**
 * Clears any active periodic check timer.
 * Used when performing logout, IP change event, or service cleanup.
 */
function disconnectService () {
  if (isDebug) console.log('[Xpoint] Clear periodic check interval.')
  clearTimeout(timeoutInterval)
  return {}
}

/**
 * Checks whether all required geolocation permissions are granted.
 *
 * @param {String|Number} user - User ID.
 * @param {String|Boolean} sessionId - Session identifier (unused).
 * @param {String} reason - Reason for permission check.
 * @returns {Promise<Boolean>} True if permissions are OK; false otherwise.
 */
async function checkPermissionOrPlugin (user, sessionId = false, reason) {
  try {
    let permissions = await xpjsService.checkPermissions()
    if (permissions.length > 0) {
      if (isDebug) console.log('[Xpoint] Permissions error.', permissions)
      showPermissionDialog(user, reason)
      return false
    }
    if (isDebug) console.log('[Xpoint] Permissions granted.')
    return true
  } catch (error) {
    throw error
  }
}

/**
 * Displays browser permission dialog / error handler.
 */
async function showPermissionDialog (user, reason) {
  let getBrowser = await utils.detectBrowserModern()
  utils.errorHandler('BROWSER_LOCATION_DENIED', 'BROWSER_LOCATION_DENIED', user, reason, getBrowser)
}

/**
 * Placeholder for a future download implementation.
 * Could be used to trigger installation or download of XPoint Lite plugin/app.
 */
function download () {
  return {}
}

/**
 * Validates stored token expiration and schedules the next geolocation check.
 *
 * Reads data from localStorage, compares timestamp to expiration,
 * and delegates to startStatusUpdate() with the calculated timeout.
 */
function checkTokenValidity (options) {
  if (isDebug) console.log('[Xpoint] Checking geolocation token validity...')
  const raw = localStorage.getItem('tgc')
  if (!raw) {
    startStatusUpdate(options, false)
    return false
  }

  let tgc
  try {
    tgc = JSON.parse(raw)
  } catch (e) {
    startStatusUpdate(options, false)
    return false
  }
  let timeout = 0
  const expiration = moment.utc(tgc.expT)
  const expirationUTC = moment(expiration).utc()
  const xpointExpiration = moment().utc()
  let timer = tgc.cf
  // Calculate differences
  const diffMs = expirationUTC.diff(xpointExpiration)
  // const diffSeconds = expirationUTC.diff(xpointExpiration, 'seconds')
  // const diffMin = diffMs / 1000 / 60
  // const diffMinutes = Number(diffMin.toFixed(2))
  // console.log('XPOINT different', {diffMs, diffSeconds, diffMinutes})
  if (diffMs > timer) {
    console.log('[Xpoint] use buffered time.', timer)
    timeout = timer
  } else if (diffMs > 0 && diffMs <= timer) {
    timeout = diffMs
    console.log('[Xpoint] set diff as timer.', timeout)
  } else {
    console.log('[Xpoint] token expired.')
    timeout = 0
  }
  startStatusUpdate(options, false, timeout)
}

/**
 * Singleton accessor for GeolocationManager instance.
 */
const managerInstance = {
  get geolocationManager () {
    if (!geoManager) geoManager = GeolocationManager.getInstance(config.app.autoconf.GEOLOCATION.PROVIDER, Vue.prototype.$geolocationDebug)
    return geoManager
  }
}

export default {
  checkStatus,
  disconnectService,
  checkPermissionOrPlugin,
  download,
  startStatusUpdate
}



// WEBPACK FOOTER //
// ./src/library/geolocation/services/providers/xpoint/lite.js